import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Search, Building2, Star, TrendingUp, TrendingDown,
  Mail, Phone, Globe, Zap, CreditCard, CheckCircle2,
  XCircle, AlertTriangle, RefreshCw, Plus, MoreHorizontal,
  ChevronRight, Loader2, Activity, BarChart3, PieChart, Download,
  Layers, Clock, ArrowUpRight, ArrowDownRight, Database,
  Plug, ShieldCheck, Users, Target, Filter
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  Cell, PieChart as RePieChart, Pie, Sector, Label
} from 'recharts';

// ─── Enterprise Design Tokens ───────────────────────────────
const C = {
  textPrimary: 'var(--text-primary)',
  textSecondary: 'var(--text-secondary)',
  textTertiary: 'var(--text-tertiary)',
  border: 'var(--border-primary)',
  surface: 'var(--bg-secondary)',
  surfaceAlt: 'var(--bg-tertiary)',
  accent: 'var(--accent-primary)',
  success: 'var(--success)',
  warning: 'var(--warning)',
  error: 'var(--error)',
  info: 'var(--info)',
  radius: '6px',
  cardPadding: '20px',
  fontSize: 'var(--font-size-base, 14px)',
  small: 'var(--font-size-sm, 13px)',
  micro: 'var(--font-size-xs, 12px)',
  headerSize: 'var(--font-size-xs, 11px)',
  headerWeight: 500,
  spacing: 16,
};

// ─── Small Reusable Components ─────────────────────────────
const KpiCard = ({ label, value, trend, trendLabel, icon: Icon, onClick, subtitle, accent = C.accent }) => (
  <div
    onClick={onClick}
    style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderLeft: `3px solid ${accent}`,
      borderRadius: C.radius,
      padding: `${C.cardPadding} ${C.cardPadding} ${C.cardPadding} ${parseInt(C.cardPadding) - 3}px`,
      cursor: onClick ? 'pointer' : 'default',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      minWidth: 0,
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    <Icon size={48} style={{ position: 'absolute', right: 8, bottom: -8, opacity: 0.06, transform: 'rotate(-15deg)' }} />
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
      <span style={{ fontSize: 'var(--font-size-xs, 11px)', fontWeight: C.headerWeight, color: C.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </span>
      <Icon size={14} style={{ color: C.textTertiary }} />
    </div>
    <div style={{ fontSize: 'var(--font-size-xl, 20px)', fontWeight: 600, color: C.textPrimary, lineHeight: 1.2, position: 'relative' }}>
      {value}
    </div>
    {trend !== undefined && (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 'var(--font-size-sm, 13px)', position: 'relative' }}>
        {trend >= 0 ? (
          <ArrowUpRight size={12} style={{ color: C.success }} />
        ) : (
          <ArrowDownRight size={12} style={{ color: C.error }} />
        )}
        <span style={{ color: trend >= 0 ? C.success : C.error, fontWeight: 500 }}>
          {Math.abs(trend)}%
        </span>
        {trendLabel && <span style={{ color: C.textTertiary }}>{trendLabel}</span>}
      </div>
    )}
    {subtitle && <div style={{ fontSize: 'var(--font-size-xs, 12px)', color: C.textTertiary, position: 'relative' }}>{subtitle}</div>}
  </div>
);

const Panel = ({ title, icon: Icon, children, right, style }) => (
  <div
    style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: C.radius,
      padding: C.cardPadding,
      display: 'flex',
      flexDirection: 'column',
      gap: C.spacing,
      minHeight: 0,
      ...style,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {Icon && <Icon size={14} style={{ color: C.textSecondary }} />}
        <h3 style={{ fontSize: 'var(--font-size-lg, 16px)', fontWeight: 600, color: C.textPrimary, margin: 0 }}>
          {title}
        </h3>
      </div>
      {right}
    </div>
    <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
  </div>
);

const UsageBar = ({ label, used, limit, reverse = false }) => {
  const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const color = reverse 
    ? (pct >= 80 ? C.success : pct >= 50 ? C.warning : C.error)
    : (pct >= 95 ? C.error : pct >= 80 ? C.warning : C.accent);
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--font-size-sm, 13px)', marginBottom: 4 }}>
        <span style={{ color: C.textSecondary }}>{label}</span>
        <span style={{ color: C.textTertiary, fontWeight: 500 }}>
          {used.toLocaleString()} / {limit >= 999999 ? '∞' : limit.toLocaleString()}
        </span>
      </div>
      <div style={{ height: 6, background: C.surfaceAlt, borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.3s ease' }} />
      </div>
    </div>
  );
};

const LoadingDots = () => (
  <span className="loading-dots" style={{ display: 'inline-flex', gap: 2 }}>
    <span>.</span><span>.</span><span>.</span>
  </span>
);

const StatusPill = ({ status }) => {
  const map = {
    completed: { bg: 'var(--success-bg)', color: C.success, label: 'Completed' },
    failed: { bg: 'var(--error-bg)', color: C.error, label: 'Failed' },
    running: { bg: 'var(--info-bg)', color: C.info, label: 'Running' },
    queued: { bg: 'var(--warning-bg)', color: C.warning, label: 'Queued' },
  };
  const s = map[status] || map.completed;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 12, fontSize: 'var(--font-size-xs, 12px)',
      background: s.bg, color: s.color, fontWeight: 500,
    }}>
      {s.label}
    </span>
  );
};



const dashboardStyles = `
  .dashboard-grid {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 12px;
  }
  .dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    flex-wrap: wrap;
    gap: 12px;
  }
  .dashboard-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }
  @media (max-width: 1024px) {
    .dashboard-grid > * {
      grid-column: span 6 !important;
    }
  }
  @media (max-width: 768px) {
    .dashboard-grid > * {
      grid-column: span 12 !important;
    }
    .page-content {
      padding: 6px !important;
    }
    .kpi-grid {
      grid-template-columns: repeat(2, 1fr) !important;
      gap: 10px !important;
    }
    .dashboard-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
    }
    .dashboard-actions {
      width: 100%;
      flex-wrap: wrap;
    }
    .dashboard-actions button {
      flex: 1;
      min-width: 100px;
      justify-content: center;
    }
    .dashboard-actions button:first-child {
      flex: 0 0 100%;
      width: 100%;
      min-width: 100%;
    }
  }
`;

// ─── Main Dashboard Component ───────────────────────────────
export default function Home() {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    quality: 'all',
    minRating: '',
    hasEmail: false,
    hasPhone: false,
    hasWebsite: false,
    dateRange: '30d',
  });

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const [statsRes, integrationsRes] = await Promise.all([
        api.get('/api/dashboard/stats'),
        api.get('/api/integrations').catch(() => ({ data: [] })),
      ]);
      setData(statsRes.data);
      setIntegrations(integrationsRes.data?.integrations || integrationsRes.data || []);
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh quota data every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleGenerateReport = async () => {
    // Plan gating: free and starter users get upgrade prompt
    const allowedPlans = ['growth', 'pro', 'enterprise'];
    if (!allowedPlans.includes(userPlan)) {
      toast.warning('PDF reports are available on Growth plans and above.', {
        description: 'Upgrade to Growth to unlock professional PDF reports.',
        action: {
          label: 'Upgrade',
          onClick: () => navigate('/dashboard/billing'),
        },
      });
      return;
    }
    setDownloadingReport(true);
    try {
      const res = await api.get('/api/leads/report?period=weekly&format=pdf', { responseType: 'blob' });
      if (res.data && res.data.size > 0) {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `iLeadX_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Report downloaded!');
      } else {
        toast.error('Failed to generate report. Please try again.');
      }
    } catch (err) {
      toast.error('Failed to generate report. Please try again.');
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleRefresh = () => fetchData(true);

  if (loading) {
    return (
      <div className="page-content" style={{ maxWidth: '100%' }}>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', color: C.textTertiary }} />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page-content" style={{ maxWidth: '100%' }}>
        <div style={{ textAlign: 'center', padding: 60, color: C.textSecondary }}>
          <Activity size={24} style={{ marginBottom: 12, color: C.textTertiary }} />
          <p>Unable to load dashboard data.</p>
          <button onClick={handleRefresh} style={{ marginTop: 12, background: 'transparent', border: `1px solid ${C.border}`, padding: '6px 16px', borderRadius: C.radius, color: C.accent, cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const {
    total_leads, scored_leads, avg_score, hot_leads, warm_leads, cold_leads,
    industries, recent_searches, enrichment, usage, weekly_trend, avg_deal_value,
  } = data;

  const userPlan = data?.usage?.plan || 'free';

  const pipelineValue = total_leads * (avg_deal_value || 0);
  const coverageRate = enrichment?.website_rate || 0;
  const emailRate = enrichment?.email_rate || 0;
  const phoneRate = enrichment?.phone_rate || 0;
  const socialRate = enrichment?.social_rate || enrichment?.with_social > 0 ? Math.round((enrichment?.with_social || 0) / Math.max(total_leads, 1) * 100) : 0;

  const qualityData = [
    { name: 'Hot', value: hot_leads, color: C.success },
    { name: 'Warm', value: warm_leads, color: C.warning },
    { name: 'Cold', value: cold_leads, color: C.error },
  ];

  const trendData = (weekly_trend || []).map(d => ({ date: d.date?.slice(5) || d.date, count: d.count }));

  const industryData = (industries || []).slice(0, 6).map(i => ({ name: i.name, value: i.count }));

  const integrationList = (integrations || []).filter(int => 
    int.connected === true || int.connected === 1 || int.status === 'connected'
  );

  return (
    <>
      <style>{dashboardStyles}</style>
      <div className="page-content" style={{ maxWidth: '100%', padding: 8 }}>
      {/* Header row */}
      <div className="dashboard-header">
        <div>
          <h1 style={{ fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px', letterSpacing: '-0.02em' }}>
            Executive Dashboard
          </h1>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0 }}>
            Lead intelligence overview for your pipeline
          </p>
        </div>
        <div className="dashboard-actions">
          <button
            onClick={() => navigate('/dashboard/new-search')}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'linear-gradient(135deg, var(--accent-amber), #D97706)',
              border: 'none',
              padding: '10px 20px', borderRadius: C.radius,
              color: '#0F172A', cursor: 'pointer',
              fontSize: 14, fontWeight: 700,
              boxShadow: '0 8px 28px rgba(245,158,11,0.35)',
              whiteSpace: 'nowrap',
            }}
          >
            <Search size={16} /> New Search
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: showFilters ? C.surfaceAlt : 'transparent',
              border: `1px solid ${C.border}`,
              padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
              color: showFilters ? C.accent : C.textSecondary,
              fontSize: 10, fontWeight: 500,
              height: 24, lineHeight: '18px',
            }}
          >
            <Filter size={10} />
            Filters
          </button>
          <button
            onClick={handleGenerateReport}
            disabled={downloadingReport}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'transparent', border: `1px solid ${C.border}`,
              padding: '3px 8px', borderRadius: 4, cursor: downloadingReport ? 'default' : 'pointer',
              color: C.textSecondary, fontSize: 10, fontWeight: 500,
              opacity: downloadingReport ? 0.6 : 1,
              height: 24, lineHeight: '18px',
            }}
          >
            <Download size={10} />
            {downloadingReport ? (
              <>Generating<LoadingDots /></>
            ) : (
              'Report (PDF)'
            )}
          </button>
          <button
            onClick={handleRefresh}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: 'transparent', border: `1px solid ${C.border}`,
              padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
              color: C.textSecondary, fontSize: 10, fontWeight: 500,
              height: 24, lineHeight: '18px',
            }}
          >
            <RefreshCw size={10} style={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* KPI Row */}
      <div className="kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        <KpiCard
          label="Total Leads" value={total_leads.toLocaleString()} icon={Users} accent={C.accent}
          onClick={() => navigate('/dashboard/history')}
          subtitle="All time"
        />
        <KpiCard
          label="Avg Lead Score" value={avg_score?.toFixed(1) || '0'} icon={Target} accent={C.info}
          onClick={() => navigate('/dashboard/history')}
          subtitle={`${scored_leads} scored`}
        />
        <KpiCard
          label="Hot Leads" value={hot_leads.toLocaleString()} icon={TrendingUp} accent={C.success}
          onClick={() => navigate('/dashboard/history?quality=hot')}
          subtitle={`${warm_leads} warm, ${cold_leads} cold`}
        />
        <KpiCard
          label="Est. Pipeline" value={`$${Math.round(pipelineValue).toLocaleString()}`} icon={BarChart3} accent={C.warning}
          onClick={() => navigate('/dashboard/history')}
          subtitle={`Avg deal $${avg_deal_value?.toLocaleString() || 0}`}
        />
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: C.radius, padding: C.cardPadding, marginBottom: 16,
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12,
        }}>
          <div>
            <label style={{ fontSize: 'var(--font-size-xs, 12px)', color: C.textTertiary, display: 'block', marginBottom: 4 }}>LEAD QUALITY</label>
            <select
              value={filters.quality}
              onChange={(e) => setFilters({ ...filters, quality: e.target.value })}
              style={{ width: '100%', padding: '6px 8px', borderRadius: C.radius, border: `1px solid ${C.border}`, background: C.surfaceAlt, color: C.textPrimary, fontSize: 'var(--font-size-sm, 13px)' }}
            >
              <option value="all">All Leads</option>
              <option value="hot">Hot (≥ 70)</option>
              <option value="warm">Warm (40-69)</option>
              <option value="cold">Cold (&lt; 40)</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 'var(--font-size-xs, 12px)', color: C.textTertiary, display: 'block', marginBottom: 4 }}>MIN RATING</label>
            <select
              value={filters.minRating}
              onChange={(e) => setFilters({ ...filters, minRating: e.target.value })}
              style={{ width: '100%', padding: '6px 8px', borderRadius: C.radius, border: `1px solid ${C.border}`, background: C.surfaceAlt, color: C.textPrimary, fontSize: 'var(--font-size-sm, 13px)' }}
            >
              <option value="">Any Rating</option>
              <option value="4.0">4.0+</option>
              <option value="4.5">4.5+</option>
            </select>
          </div>
          <div>
            <label style={{ fontSize: 'var(--font-size-xs, 12px)', color: C.textTertiary, display: 'block', marginBottom: 4 }}>DATE RANGE</label>
            <select
              value={filters.dateRange}
              onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}
              style={{ width: '100%', padding: '6px 8px', borderRadius: C.radius, border: `1px solid ${C.border}`, background: C.surfaceAlt, color: C.textPrimary, fontSize: 'var(--font-size-sm, 13px)' }}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
            <button
              onClick={() => {
                const params = new URLSearchParams();
                if (filters.quality === 'hot') params.set('quality', 'hot');
                if (filters.quality === 'warm') params.set('score_min', '40');
                if (filters.quality === 'cold') params.set('score_max', '39');
                if (filters.minRating) params.set('min_rating', filters.minRating);
                if (filters.dateRange !== 'all') params.set('date_range', filters.dateRange);
                navigate(`/dashboard/history?${params.toString()}`);
              }}
              style={{
                padding: '6px 14px', borderRadius: C.radius,
                background: C.accent, border: 'none', color: '#fff',
                cursor: 'pointer', fontSize: 'var(--font-size-sm, 13px)', fontWeight: 600,
              }}
            >
              Apply Filters
            </button>
            <button
              onClick={() => {
                setFilters({ quality: 'all', minRating: '', hasEmail: false, hasPhone: false, hasWebsite: false, dateRange: '30d' });
                navigate('/dashboard/history');
              }}
              style={{
                padding: '6px 14px', borderRadius: C.radius,
                background: 'transparent', border: `1px solid ${C.border}`,
                color: C.textSecondary, cursor: 'pointer', fontSize: 'var(--font-size-sm, 13px)',
              }}
            >
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Main grid */}
      <div className="dashboard-grid">
        {/* Pipeline Quality Donut */}
        <div style={{ gridColumn: 'span 4' }}>
          <Panel title="Pipeline Quality" icon={PieChart}>
            <ResponsiveContainer width="100%" height={180}>
              <RePieChart>
                <Pie
                  data={qualityData}
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                >
                  {qualityData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                  <Label
                    content={({ viewBox }) => {
                      const { cx, cy } = viewBox;
                      return (
                        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle">
                          <tspan x={cx} dy="-8" style={{ fontSize: 18, fontWeight: 600, fill: C.textPrimary }}>
                            {total_leads}
                          </tspan>
                          <tspan x={cx} dy="16" style={{ fontSize: 'var(--font-size-xxs, 11px)', fill: C.textTertiary }}>
                            Leads
                          </tspan>
                        </text>
                      );
                    }}
                  />
                </Pie>
                <Tooltip
                  contentStyle={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: C.radius, fontSize: 'var(--font-size-sm, 13px)' }}
                  formatter={(value, name) => [value, name]}
                />
              </RePieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 4 }}>
              {qualityData.map(q => (
                <div key={q.name} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: q.color }} />
                  <span style={{ fontSize: 'var(--font-size-xs, 12px)', color: C.textSecondary }}>
                    {q.name} ({q.value})
                  </span>
                </div>
              ))}
            </div>
          </Panel>
        </div>

        {/* Search Performance Trend */}
        <div style={{ gridColumn: 'span 8' }}>
          <Panel
            title="Search Activity (30d)" icon={Activity}
            right={<span style={{ fontSize: 'var(--font-size-xs, 12px)', color: C.textTertiary }}>Updated just now</span>}
          >
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="searchGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={C.accent} stopOpacity={0.15} />
                    <stop offset="95%" stopColor={C.accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" />
                <XAxis dataKey="date" tick={{ fontSize: 'var(--font-size-xxs, 11px)', fill: C.textTertiary }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 'var(--font-size-xxs, 11px)', fill: C.textTertiary }} tickLine={false} axisLine={false} width={30} />
                <Tooltip
                  contentStyle={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: C.radius, fontSize: 'var(--font-size-sm, 13px)' }}
                />
                <Area
                  type="monotone" dataKey="count" name="Searches"
                  stroke={C.accent} fill="url(#searchGradient)" strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Panel>
        </div>

        {/* Industry Breakdown */}
        <div style={{ gridColumn: 'span 6' }}>
          <Panel title="Top Industries" icon={Layers}>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={industryData} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-primary)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 'var(--font-size-xxs, 11px)', fill: C.textTertiary }} tickLine={false} axisLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 'var(--font-size-xs, 12px)', fill: C.textSecondary }} width={120} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: C.radius, fontSize: 'var(--font-size-sm, 13px)' }}
                />
                <Bar dataKey="value" name="Leads" fill={C.accent} radius={[0, 4, 4, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        </div>

        {/* Enrichment Health */}
        <div style={{ gridColumn: 'span 6' }}>
          <Panel title="Enrichment Health" icon={Database}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <UsageBar label="Website" used={Math.round(coverageRate)} limit={100} reverse />
              <UsageBar label="Email" used={Math.round(emailRate)} limit={100} reverse />
              <UsageBar label="Phone" used={Math.round(phoneRate)} limit={100} reverse />
              <UsageBar label="Social" used={Math.round(socialRate)} limit={100} reverse />
            </div>
            <div style={{ fontSize: 'var(--font-size-xs, 12px)', color: C.textTertiary, marginTop: 4 }}>
              Data completeness based on discovered fields.
            </div>
          </Panel>
        </div>

        {/* Recent Searches Table */}
        <div style={{ gridColumn: 'span 7', minWidth: 0, overflow: 'hidden' }}>
          <Panel title="Recent Searches" icon={Search}>
            <div style={{ overflowX: 'auto', overflowY: 'hidden', maxWidth: '100%', minWidth: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-base, 14px)', minWidth: 400 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                  <th style={{ textAlign: 'left', padding: '10px 12px', color: C.textTertiary, fontWeight: 500, fontSize: 'var(--font-size-xs, 12px)' }}>QUERY</th>
                  <th style={{ textAlign: 'right', padding: '10px 12px', color: C.textTertiary, fontWeight: 500, fontSize: 'var(--font-size-xs, 12px)' }}>LEADS</th>
                  <th style={{ textAlign: 'right', padding: '10px 12px', color: C.textTertiary, fontWeight: 500, fontSize: 'var(--font-size-xs, 12px)' }}>DATE</th>
                  <th style={{ textAlign: 'right', padding: '10px 12px', color: C.textTertiary, fontWeight: 500, fontSize: 'var(--font-size-xs, 12px)' }}>STATUS</th>
                </tr>
              </thead>
              <tbody>
                {(recent_searches || []).slice(0, 5).map((s, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid var(--border-primary)` }}>
                    <td style={{ padding: '10px 12px', color: C.textPrimary, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.query}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: C.textSecondary }}>{s.results || 0}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: C.textTertiary, fontSize: 'var(--font-size-sm, 13px)' }}>
                      {s.last_run ? new Date(s.last_run).toLocaleDateString() : '-'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                      <StatusPill status="completed" />
                    </td>
                  </tr>
                ))}
                {(!recent_searches || recent_searches.length === 0) && (
                  <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20, color: C.textTertiary }}>No searches yet</td></tr>
                )}
              </tbody>
            </table>
            </div>
            {recent_searches?.length > 0 && (
              <button
                onClick={() => navigate('/dashboard/history')}
                style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 4, background: 'transparent', border: 'none', color: C.accent, cursor: 'pointer', fontSize: 'var(--font-size-sm, 13px)', alignSelf: 'flex-end' }}
              >
                View all <ChevronRight size={12} />
              </button>
            )}
          </Panel>
        </div>

        {/* Integration Status */}
        <div style={{ gridColumn: 'span 5' }}>
          <Panel title="Integrations" icon={Plug}>
            {integrationList.length > 0 ? (
              <>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {integrationList.map((int, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: C.surfaceAlt, borderRadius: C.radius }}>
                      <CheckCircle2 size={14} style={{ color: C.success }} />
                      <span style={{ fontSize: 'var(--font-size-sm, 13px)', color: C.textPrimary, flex: 1 }}>{int.name || int.integration_id || 'Integration'}</span>
                      <span style={{ fontSize: 'var(--font-size-xs, 12px)', color: C.textTertiary }}>
                        {int.last_synced ? new Date(int.last_synced).toLocaleString() : 'Connected'}
                      </span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => navigate('/dashboard/integrations')}
                  style={{
                    marginTop: 10, alignSelf: 'flex-start',
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: 'transparent', border: `1px dashed ${C.border}`,
                    padding: '4px 12px', borderRadius: C.radius,
                    color: C.textSecondary, cursor: 'pointer',
                    fontSize: 'var(--font-size-xs, 12px)', fontWeight: 500,
                  }}
                >
                  <Plus size={12} /> Add more
                </button>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: 20, color: C.textTertiary, fontSize: 'var(--font-size-sm, 13px)' }}>
                No integrations connected.
                <button
                  onClick={() => navigate('/dashboard/integrations')}
                  style={{ display: 'block', margin: '8px auto 0', background: 'transparent', border: `1px solid ${C.border}`, padding: '4px 12px', borderRadius: C.radius, color: C.accent, cursor: 'pointer', fontSize: 'var(--font-size-xs, 12px)' }}
                >
                  Connect Integration
                </button>
              </div>
            )}
          </Panel>
        </div>

        {/* Usage & Quota */}
        <div style={{ gridColumn: 'span 12' }}>
          <Panel title="Usage & Quota" icon={CreditCard}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              <UsageBar label="Searches" used={usage?.searches?.used || 0} limit={usage?.searches?.limit || 0} />
              <UsageBar label="AI Scores" used={usage?.ai_scores?.used || 0} limit={usage?.ai_scores?.limit || 0} />
              <UsageBar label="Exports" used={usage?.exports?.used || 0} limit={usage?.exports?.limit || 0} />
            </div>
          </Panel>
        </div>
      </div>
      </div>
    </>
  );
}
